import { Box, Paper, Typography, IconButton, Tooltip, Badge } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  AccountTree,
  AutoFixHigh,
  EditNote,
  ContentCut,
  ChevronLeft,
  HelpOutline,
  FindReplace
} from '@mui/icons-material';
import { usePanelStyles } from '@/styles/utilities/hooks';
import BrowserPanel from '../panels/BrowserPanel';
import PatternExtractionPanel from '../panels/PatternExtractionPanel';
import MetadataEditingPanel from '../panels/MetadataEditingPanel';
import FMOInputGeneratorPanel from '../panels/FMOInputGeneratorPanel';
import FindReplacePanel from '../panels/FindReplacePanel';

const sidebarButtons = [
  { id: 'browser', icon: <AccountTree />, tooltip: 'Browser' },
  { id: 'pattern', icon: <AutoFixHigh />, tooltip: 'Pattern Extraction' },
  { id: 'edit', icon: <EditNote />, tooltip: 'Metadata Editing' },
  { id: 'findreplace', icon: <FindReplace />, tooltip: 'Find and Replace' },
  { id: 'fmo', icon: <ContentCut />, tooltip: 'FMO Input Generator' },
];

export default function LeftSidebar({
  // Panel state
  activeSidebarView,
  isLeftPanelCollapsed,
  setIsLeftPanelCollapsed,
  onSidebarClick,

  // Browser panel props
  treeData,
  treeSelection,
  onTreeSelectionChange,
  expandedItems,
  onExpandedItemsChange,
  loading,
  filteredCount,
  selectedCount,
  totalCount,
  onClearFilter,
  showBrowserHelp,
  onBrowserHelpToggle,

  // Pattern panel props
  patternPanelRef,
  selectedRows,
  uploadFiles,
  selectedUploadRows,
  onPatternApply,
  onPreviewUpdate,
  previewMode,

  // Metadata panel props
  metadataPanelRef,
  allPrepFiles,

  // Find Replace panel props
  findReplacePanelRef,

  // FMO panel props
  fmoPanelRef,
  preps,
  onFragmentationStart,
}) {
  const handleSidebarClick = (buttonId) => {
    onSidebarClick(buttonId);
  };

  const theme = useTheme();
  const panelStyles = usePanelStyles();

  const getPanelTitle = () => {
    switch (activeSidebarView) {
      case 'browser': return 'Browser';
      case 'pattern': return 'Pattern Extraction';
      case 'edit': return 'Metadata Editing';
      case 'findreplace': return 'Find and Replace';
      case 'fmo': return 'FMO Input Generator';
      default: return '';
    }
  };

  const renderHelpButton = () => {
    switch (activeSidebarView) {
      case 'browser':
        return (
          <Tooltip title="Show help">
            <IconButton 
              size={panelStyles.headerIconButton.size}
              sx={panelStyles.headerIconButton}
              onClick={() => onBrowserHelpToggle(true)}
            >
              <HelpOutline sx={{ fontSize: theme.dimensions.icon.small }} />
            </IconButton>
          </Tooltip>
        );
      case 'pattern':
        return (
          <Tooltip title="Show help">
            <IconButton 
              size={panelStyles.headerIconButton.size}
              sx={panelStyles.headerIconButton}
              onClick={() => patternPanelRef.current?.toggleHelp()}
            >
              <HelpOutline sx={{ fontSize: theme.dimensions.icon.small }} />
            </IconButton>
          </Tooltip>
        );
      case 'edit':
        return (
          <Tooltip title="Show help">
            <IconButton
              size={panelStyles.headerIconButton.size}
              sx={panelStyles.headerIconButton}
              onClick={() => metadataPanelRef.current?.toggleHelp()}
            >
              <HelpOutline sx={{ fontSize: theme.dimensions.icon.small }} />
            </IconButton>
          </Tooltip>
        );
      case 'findreplace':
        return (
          <Tooltip title="Show help">
            <IconButton
              size={panelStyles.headerIconButton.size}
              sx={panelStyles.headerIconButton}
              onClick={() => findReplacePanelRef.current?.toggleHelp()}
            >
              <HelpOutline sx={{ fontSize: theme.dimensions.icon.small }} />
            </IconButton>
          </Tooltip>
        );
      case 'fmo':
        return (
          <Tooltip title="Show help">
            <IconButton
              size={panelStyles.headerIconButton.size}
              sx={panelStyles.headerIconButton}
              onClick={() => fmoPanelRef.current?.toggleHelp()}
            >
              <HelpOutline sx={{ fontSize: theme.dimensions.icon.small }} />
            </IconButton>
          </Tooltip>
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Sidebar Buttons */}
      <Box sx={{
        width: theme.spacing(5), // 40px at standard density
        bgcolor: 'background.paper',
        borderRight: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0,
      }}>
        {sidebarButtons.map((button) => (
          <Tooltip 
            key={button.id} 
            title={
              activeSidebarView === button.id && !isLeftPanelCollapsed 
                ? `Hide ${button.tooltip}` 
                : button.tooltip
            } 
            placement="right"
          >
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              {activeSidebarView === button.id && !isLeftPanelCollapsed && (
                <Box
                  sx={{
                    position: 'absolute',
                    left: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: theme.spacing(0.375), // 3px at standard density
                    height: theme.spacing(4), // 32px at standard density
                    bgcolor: 'primary.main',
                    borderRadius: `0 ${theme.spacing(0.25)} ${theme.spacing(0.25)} 0`,
                  }}
                />
              )}
              <IconButton
                onClick={() => handleSidebarClick(button.id)}
                sx={{
                  width: theme.spacing(5),
                  height: theme.spacing(5),
                  color: activeSidebarView === button.id && !isLeftPanelCollapsed ? 'primary.main' : 'text.secondary',
                  bgcolor: activeSidebarView === button.id && !isLeftPanelCollapsed ? 'action.selected' : 'transparent',
                  '&:hover': {
                    color: 'text.primary',
                    bgcolor: 'action.hover',
                  },
                  borderRadius: 0,
                  '& .MuiSvgIcon-root': {
                    fontSize: theme.dimensions.icon.medium,
                  },
                }}
              >
                {button.id === 'browser' && treeSelection && treeSelection.length > 0 ? (
                  <Badge
                    variant="dot"
                    color="primary"
                    sx={{
                      '& .MuiBadge-dot': {
                        width: theme.spacing(0.75), // 6px at standard density
                        height: theme.spacing(0.75),
                        minWidth: theme.spacing(0.75),
                        borderRadius: '50%',
                      }
                    }}
                  >
                    {button.icon}
                  </Badge>
                ) : (
                  button.icon
                )}
              </IconButton>
            </Box>
          </Tooltip>
        ))}
      </Box>

      {/* Left Panel */}
      {!isLeftPanelCollapsed && (
        <Paper sx={{ 
          width: theme.dimensions?.panel?.leftSidebarWidth || 480, 
          minWidth: theme.dimensions?.panel?.leftSidebarMinWidth || 400,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 0,
          borderRight: '1px solid',
          borderColor: 'divider'
        }}>
          <Box sx={panelStyles.draggableHeader}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography sx={panelStyles.headerTitle}>
                {getPanelTitle()}
              </Typography>
              {renderHelpButton()}
            </Box>
            <Tooltip title="Collapse panel">
              <IconButton 
                size="small" 
                onClick={() => setIsLeftPanelCollapsed(true)}
                sx={{ padding: theme.spacing(0.5) }}
              >
                <ChevronLeft sx={{ fontSize: theme.dimensions.icon.small }} />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {/* Browser Panel */}
            {activeSidebarView === 'browser' && (
              <BrowserPanel
                treeData={treeData}
                treeSelection={treeSelection}
                onTreeSelectionChange={onTreeSelectionChange}
                expandedItems={expandedItems}
                onExpandedItemsChange={onExpandedItemsChange}
                loading={loading}
                filteredCount={filteredCount}
                selectedCount={selectedCount}
                totalCount={totalCount}
                onClearFilter={onClearFilter}
                showHelp={showBrowserHelp}
                onHelpToggle={onBrowserHelpToggle}
              />
            )}

            {/* Pattern Extraction Panel */}
            {activeSidebarView === 'pattern' && (
              <PatternExtractionPanel
                ref={patternPanelRef}
                selectedRows={selectedRows}
                uploadFiles={uploadFiles}
                selectedUploadRows={selectedUploadRows}
                onPatternApply={onPatternApply}
                onPreviewUpdate={onPreviewUpdate}
                previewMode={previewMode}
              />
            )}

            {/* Metadata Editing Panel */}
            {activeSidebarView === 'edit' && (
              <MetadataEditingPanel
                ref={metadataPanelRef}
                selectedRows={selectedRows}
                uploadFiles={uploadFiles}
                selectedUploadRows={selectedUploadRows}
                onPreviewUpdate={onPreviewUpdate}
                previewMode={previewMode}
                allPrepFiles={allPrepFiles}
              />
            )}

            {/* Find and Replace Panel */}
            {activeSidebarView === 'findreplace' && (
              <FindReplacePanel
                ref={findReplacePanelRef}
                selectedRows={selectedRows}
                uploadFiles={uploadFiles}
                selectedUploadRows={selectedUploadRows}
                onPreviewUpdate={onPreviewUpdate}
                previewMode={previewMode}
                allPrepFiles={allPrepFiles}
              />
            )}

            {/* FMO Input Generator Panel */}
            {activeSidebarView === 'fmo' && (
              <FMOInputGeneratorPanel 
                ref={fmoPanelRef}
                selectedRows={selectedRows}
                preps={preps}
                onFragmentationStart={onFragmentationStart}
              />
            )}
          </Box>
        </Paper>
      )}
    </Box>
  );
}